import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, X, Search } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Single-select combobox with real type-to-filter.
 *
 * Built WITHOUT Radix Popover on purpose — Radix portals into <body> and
 * fights Leaflet's stacking contexts on iPad Safari. This component keeps
 * the dropdown inside its own DOM subtree as an absolutely-positioned
 * sibling, so z-index is local and predictable.
 *
 * Props:
 *   value:       string | null  — currently selected value
 *   onChange:    (string | null) => void
 *   options:     string[]
 *   allLabel:    string — label for the "no filter" sentinel (default "All")
 *   placeholder: string
 *   testId:      string
 *   zIndex:      number — local stacking number for the open menu (default 1500)
 */
export function SingleSelectCombobox({
  value,
  onChange,
  options = [],
  allLabel = "All",
  placeholder = "Search…",
  testId,
  zIndex = 1500,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Focus the input the moment the menu opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 300);
    return options.filter((o) => o.toLowerCase().includes(q)).slice(0, 300);
  }, [options, query]);

  const choose = (val) => {
    onChange(val);
    setOpen(false);
    setQuery("");
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Index 0 = the "All" sentinel; 1..filtered.length = real options.
      if (highlight === 0) choose(null);
      else choose(filtered[highlight - 1]);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "w-full h-9 flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 text-left text-sm text-zinc-100 hover:border-zinc-700 transition-colors"
        )}
      >
        <span className={value ? "" : "text-zinc-400"}>
          {value || allLabel}
        </span>
        <span className="flex items-center gap-1">
          {value && (
            <span
              role="button"
              data-testid={`${testId}-clear`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }}
              className="text-zinc-500 hover:text-rose-300"
              aria-label="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </span>
      </button>

      {open && (
        <div
          data-testid={`${testId}-menu`}
          className="absolute left-0 right-0 mt-1 rounded-md border border-zinc-700 bg-zinc-950 shadow-2xl ring-1 ring-black/40 overflow-hidden"
          style={{ zIndex }}
        >
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-zinc-800">
            <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
            <input
              ref={inputRef}
              data-testid={`${testId}-input`}
              type="text"
              placeholder={placeholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              onKeyDown={handleInputKeyDown}
              className="w-full bg-transparent text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            <button
              type="button"
              data-testid={`${testId}-opt-all`}
              onClick={() => choose(null)}
              onMouseEnter={() => setHighlight(0)}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left",
                highlight === 0 ? "bg-zinc-800/80" : "hover:bg-zinc-800/60",
                !value ? "text-emerald-200" : "text-zinc-300"
              )}
            >
              <Check
                className={cn(
                  "w-3.5 h-3.5 shrink-0",
                  !value ? "opacity-100 text-emerald-300" : "opacity-0"
                )}
              />
              {allLabel}
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-zinc-500">No matches.</div>
            ) : (
              filtered.map((opt, i) => {
                const selected = value === opt;
                const idx = i + 1; // 0 is the "all" sentinel
                return (
                  <button
                    type="button"
                    key={opt}
                    data-testid={`${testId}-opt-${opt}`}
                    onClick={() => choose(opt)}
                    onMouseEnter={() => setHighlight(idx)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left",
                      highlight === idx
                        ? "bg-zinc-800/80"
                        : "hover:bg-zinc-800/60",
                      selected ? "text-emerald-200" : "text-zinc-100"
                    )}
                  >
                    <Check
                      className={cn(
                        "w-3.5 h-3.5 shrink-0",
                        selected ? "opacity-100 text-emerald-300" : "opacity-0"
                      )}
                    />
                    <span className="truncate">{opt}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SingleSelectCombobox;
